import * as React from "react";
import { AutoCompleteSearchType, CodeValueDisplayType } from "./Enums";
import Autosuggest from "react-autosuggest/dist/Autosuggest";
import { IAutoCompleteComponent } from "./business-objects/IAutoCompleteComponent";
import "./css/index.css";
import { CodeListValue } from "./business-objects/CodeListValue";
import { getTableValuesByGroupName } from "../../../../services/Utils/IndexedDBUtils";
import { CodeListUtils } from "./business-objects/ComponentUtils/CodeListUtils";
import { DataTranscriberForm } from "../../../Forms/DataTranscriberForm/DataTranscriberForm";

export interface IAutoCompleteProps {
  component: IAutoCompleteComponent;
  value: string;
  description: string;
  onChange: () => void;
  formData: any;
  parentValue: string;
  isValid: boolean;
  data?: any;
}

export interface IAutoCompleteState {
  valueList: CodeListValue[];
  filterList: CodeListValue[];
  listLoaded: boolean;
  suggestions: any[];
  value: string;
  description: string;
  displayedValue: string;
  displayInvalidText: boolean;
  isValid: boolean;
  parentValue: string;
  shouldFocus: boolean;
  matchValue: any[];
}

export default class AutoCompleteComponentReact extends React.Component<IAutoCompleteProps, IAutoCompleteState, DataTranscriberForm> {
  private inputRef: any;
  state: IAutoCompleteState = {
    valueList: [],
    suggestions: [],
    filterList: [],
    listLoaded: false,
    value: this.props.value,
    displayedValue: "",
    description: this.props.description,
    displayInvalidText: false,
    isValid: this.props.isValid,
    parentValue: this.props.parentValue,
    shouldFocus: false,
    matchValue: []
  };

  async componentDidMount() {
    // await this.loadValues();
    await this.setValues();
  }

  componentDidUpdate(prevProps: IAutoCompleteProps, prevState: IAutoCompleteState) {
    if (this.state.shouldFocus) {
      this.inputRef.input.focus();
      this.setState({ shouldFocus: false });
    }

    if (prevState.parentValue != this.state.parentValue) {
      this.setState({ listLoaded: false }, () => {
        this.loadValues().then();
      });
    }

    if (this.state.value != prevState.value) {
      this.setValues();
      this.props.onChange();
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !(this.state.shouldFocus == true && nextState.shouldFocus == false);
  }

  getLookupGroupName = (): string => {
    const groupName = this.props.component.codeListName || this.props.component.key || "";
    return groupName === "EMSServiceName" ? "EmsService" : groupName;
  };

  preservesTranscribedValue = (): boolean =>
    ["VehicleMake", "MedicalFacilityName", "EmsService"].includes(this.getLookupGroupName());

  private findTranscribedLookupValue = (value: string, values: CodeListValue[]): CodeListValue | undefined => {
    const normalized = (value || "").trim().toLowerCase();
    if (!normalized) return undefined;
    const code = values.find(item => (item.name || "").trim().toLowerCase() === normalized);
    if (code) return code;
    const matches = values.filter(item => (item.description || "").trim().toLowerCase() === normalized);
    return matches.length === 1 ? matches[0] : undefined;
  };

  hasUnmatchedTranscribedValue = (): boolean =>
    this.preservesTranscribedValue() &&
    !this.props.component.isFreeform &&
    this.state.displayInvalidText;

  setValues = async () => {
    const results = this.state.valueList.length
      ? this.state.valueList
      : await getTableValuesByGroupName(this.getLookupGroupName());
    const valueList = results || [];

    if (this.props.component.isFreeform) {
      this.setState({ valueList, displayedValue: this.state.value || "", value: this.state.value });
    } else if (this.preservesTranscribedValue()) {
      const match = this.findTranscribedLookupValue(this.state.value, valueList);
      this.setState({
        valueList,
        displayedValue: match ? match.description : this.state.value || "",
        value: match ? match.name : this.state.value,
        displayInvalidText: !match && !!(this.state.value || "").trim()
      }, this.props.onChange);
    } else {
      //If we have the description - find the code value
      let codeListValue = valueList.find(x => x.description == this.state.value);

      //If we have the code value - find the description
      if (codeListValue == null) codeListValue = valueList.find(x => x.name == this.state.value);

      if (codeListValue) {
        this.setState({ valueList, displayedValue: codeListValue.description, value: codeListValue.name });
      } else {
        this.setState({ valueList, displayedValue: '' })
      }
    }
  };

  getSuggestions = value => {
    let matchList: string[] = this.state.valueList
      .map((codeListValue: CodeListValue) => /*codeListValue.name.trim() + " - " + */codeListValue.description.trim())
      .filter((inputValue: string) => {
        switch (this.props.component.autoCompleteSearchType) {
          case AutoCompleteSearchType.StartsWith:
            return (inputValue || "").toLowerCase().startsWith((value || "").toLowerCase());
          case AutoCompleteSearchType.ContainsText:
            return (inputValue || "").toLowerCase().indexOf((value || "").toLowerCase()) >= 0;
          default:
            return false;
        }
      });

    return [...new Set(matchList)];
  };
  private CodeListUtils: any = CodeListUtils;
  private CodeValueDisplayType: any = CodeValueDisplayType;

  loadValues = async () => {
    if (this.state.listLoaded) return;
    let list: CodeListValue[];

    getTableValuesByGroupName(this.getLookupGroupName()).then((results) => {
      this.setState({ valueList: results })
    });

    if (!this.props.component.isCascade) {
      list = await this.CodeListUtils.getFormattedCodeValueListByCodeIdAsync(
        this.props.component.codeListName,
        this.props.component.codeValueDisplayType,
        this.props.component.codeValueSaveType
      );
    } else {
      list = await this.CodeListUtils.getFormattedCodeValueListByParentTextAsync(
        this.props.component.codeListName,
        this.props.component.codeValueDisplayType,
        this.props.component.codeValueSaveType,
        this.props.component.parentCodeListName,
        this.state.parentValue || "",
        this.CodeValueDisplayType.DescriptionOnly,
        this.props.component.parentCodeValueSaveType
      );
    }

    list = list || [];

    if (
      this.props.component.isCascade &&
      !this.props.component.isFreeform &&
      this.state.displayedValue &&
      !this.checkValueInSearchList(this.state.displayedValue, list)
    ) {
      //if cascade and not free form and my value isnt in list reset value
      this.setState({ displayedValue: "", value: "", description: "" }, () => {
        this.props.onChange();
      });
    }

    this.setState({
      valueList: list,
      filterList: list,
      listLoaded: true
    });
  };

  checkValueInSearchList = (value: string, list: CodeListValue[]): boolean => {
    value = (value || "").toLowerCase();
    return (
      list &&
      list.some((item: CodeListValue) => {
        return (item.name || "").toLowerCase() === value;
      })
    );
  };

  onChange = (event, { newValue, ...other }) => {
    this.setState({
      displayedValue: newValue,
      displayInvalidText: false
    });
  };

  onSuggestionsFetchRequested = ({ value }) => {
    if (value.length >= this.props.component.minLength) {
      this.setState({
        suggestions: this.getSuggestions(value)
      });
    } else {
      this.setState({
        suggestions: []
      });
    }
  };

  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: []
    });
  };

  getSuggestionValue = suggestion => {
    return suggestion;
  };

  renderSuggestion = suggestion => <span>{suggestion}</span>;

  onBlur = (event, { highlightedSuggestion }) => {
    if (this.preservesTranscribedValue() && !this.props.component.isFreeform) {
      const enteredValue = highlightedSuggestion || this.state.displayedValue || "";
      const currentMatch = this.findTranscribedLookupValue(this.state.value, this.state.valueList);
      const match = currentMatch &&
        enteredValue.trim().toLowerCase() === (currentMatch.description || "").trim().toLowerCase()
        ? currentMatch
        : this.findTranscribedLookupValue(enteredValue, this.state.valueList);
      this.setState({
        value: match ? match.name : enteredValue,
        displayedValue: match ? match.description : enteredValue,
        displayInvalidText: !match && !!enteredValue.trim()
      }, this.props.onChange);
      return;
    }

    //find match
    let matchString: CodeListValue;

    if (highlightedSuggestion) {
      matchString = this.state.valueList.find((listValue: CodeListValue) => {
        return ((listValue.name || "").trim().toLowerCase() + " - " + (listValue.description || "").trim().toLowerCase()) === (highlightedSuggestion || "").toLowerCase();
      });
    } else {
      matchString = this.state.valueList.find((listValue: CodeListValue) => {
        return ((listValue.description || "").trim().toLowerCase()) === (this.state.displayedValue || "").toLowerCase();
      });
    }

    if (this.props.component.isFreeform) {
      //if freeform allow any input to be saved
      if (matchString) {
        this.setState({ value: matchString.name }, () => {
          this.props.onChange();
        });
      } else {
        this.setState({ value: this.state.displayedValue }, () => {
          this.props.onChange();
        });
      }
    } else if (matchString) {
      this.setState({ value: matchString.description }, () => {
        this.props.onChange();
      });
    } else {
      //not freeform, and not matched, clear input
      if (this.state.displayedValue.length > 0) {
        this.setState({ value: "", displayedValue: "", displayInvalidText: true }, () => {
          this.props.onChange();
        });
      } else {
        this.setState({ value: "", displayedValue: "", displayInvalidText: false });
      }
    }
  };

  focus = () => {
    this.setState({ shouldFocus: true });
  };

  render() {
    let className =
      this.props.component.inputClass && this.props.component.inputClass.length
        ? this.props.component.inputClass
        : "form-control";

    className = this.state.isValid ? className : className + " is-invalid";
    let { displayedValue } = this.state;
    if (displayedValue == null) displayedValue = this.state.value
    const inputProps = {
      onBlur: this.onBlur.bind(this),
      //onBlue: e => this.setState({ matchValue: e.target.value }, this.onBlur.bind(this)),
      value: displayedValue !== null ? displayedValue : "",
      onChange: this.onChange,
      disabled: false,
      placeholder: this.props.component.placeholder,
      title: this.props.component.tooltip,
      className: className,
      tabIndex: this.props.component.tabIndex,
      "aria-label": "autoSuggest",
      id: this.props.component.id + "AutoComplete",
    };
    return (
      <div>
        <Autosuggest
          suggestions={this.state.suggestions}
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested}
          getSuggestionValue={this.getSuggestionValue}
          renderSuggestion={this.renderSuggestion}
          focusInputOnSuggestionClick={true}
          inputProps={inputProps}
          ref={element => {
            this.inputRef = element;
          }}
        />
        {this.state.displayInvalidText && (
          <label className="text-danger">
            {this.hasUnmatchedTranscribedValue()
              ? "Transcribed value retained. Select a matching lookup value."
              : this.props.component.invalidSelectionText == ""
              ? "Invalid Selection"
              : this.props.component.invalidSelectionText}
          </label>
        )}
      </div>
    );
  }


}
